import React, { useEffect, useState } from "react";
import ContentLayout from "../layouts/ContentLayout";
import { isZh } from "../utils/language";
import { hasUmatPermissionAsync } from "../utils/permissionsAsync";
import PERMISSIONS from "../utils/wkCommon";
import { getUser } from "../utils/auth";
import useMediaPlayer from "../components/SongMediaPlayer";

import SongsTopBar from "../components/SongsPageTopBar";
import SongsContent from "../components/SongsPageContent";
import SongFilterPanel from "../components/SongFilterPanel";
import useSongsData from "../components/SongsPageData";

import AdminDialog from "../components/SongAdminDialog";
import SongForm from "../components/SongForm";
import AlbumForm from "../components/SongAlbumForm";
import PlaylistItemForm from "../components/SongPlaylistItemForm";
import SongFileForm from "../components/SongFileForm";
import SongShareDialog from "../components/SongShareDialog";

export default function SongsPage() {
  const user = getUser();

  const [mode, setMode] = useState("album");
  const [searchValue, setSearchValue] = useState("");
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);

  const [filters, setFilters] = useState({
    primary_language: [],
    emotion_tags: [],
    keyword_tags: [],
    no_primary_language: false,
    no_emotion_tags: false,
    no_keyword_tags: false,
    no_file: false,
    no_text_file: false,
    no_slide_file: false,
    no_audio_file: false,
    no_video_file: false,    
    sort: "updatedAt"
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);

  const [showDialog, setShowDialog] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [fileSong, setFileSong] = useState(null);
  const [shareData, setShareData] = useState(null);

  const [reloadKey, setReloadKey] = useState(0);

  const { audioRef, playingId, play } = useMediaPlayer();

  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [page, setPage] = useState(1);

  const headerActions = canEdit ? (
    <div style={{ display: "flex", gap: 8 }}>

      {/* MODE ALBUM */}
      {mode === "album" && !selectedPlaylist && (
        <button
          style={{ height: "40px" }}
          className="btn-primary"
          onClick={() => {
            setEditingPlaylist(null);
            setShowDialog("album");
          }}
        >
          {isZh() ? "+ 專輯" : "+ Album"}
        </button>
      )}

      {/* MODE PLAYLIST DETAIL */}
      {selectedPlaylist && (
        <>
          <button
            style={{ height: "40px" }}
            className="btn-primary"
            onClick={() => setShowDialog("playlistItem")}
          >
            { isZh() ? "管理播放清單內容" : "Kelola Isi Playlist" }
          </button>
        </>
      )}

      {/* MODE SEARCH SONG */}
      {(mode === "song" || mode ==="daftar") && (
        <button
            style={{ height: "40px" }}
          className="btn-primary"
          onClick={() => { setEditingData(null); setShowDialog("song") }}
        >
          {isZh() ? "+ 歌曲" : "+ Lagu"}
        </button>
      )}
    </div>
  ) : null;
    
	useEffect(() => {
		const onResize = () => setIsMobile(window.innerWidth < 768);
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);
		
  useEffect(() => {
    async function check() {
      const [basic, editor] = await Promise.all([
        hasUmatPermissionAsync(user?.email, PERMISSIONS.MEDIAFILE_BASIC),
        hasUmatPermissionAsync(user?.email, PERMISSIONS.MEDIAFILE_EDITOR)
      ]);
      setCanView(!!basic);
      setCanEdit(!!editor);
    }
    check();
  }, [user]);

  const { rows, loading, meta } = useSongsData({
    mode,
    searchValue,
    selectedPlaylist,
    appliedFilters,
    reloadKey,
    page
  });

  useEffect(() => {
    setPage(1);
  }, [mode, searchValue, appliedFilters]);

  const empty = {
    primary_language: [],
    emotion_tags: [],
    keyword_tags: []
  };

	function hasFilter() {
		return (
			appliedFilters.primary_language.length > 0 ||
			appliedFilters.emotion_tags.length > 0 ||
			appliedFilters.keyword_tags.length > 0
		);
	}

  if (!canView) {
    return <ContentLayout mainNode={<div>No access</div>} />;
  }

  return (
    <>
      <style>{`
      @media (min-width: 875px) {
        .content-body {
          grid-template-columns: var(--left-w) 1fr 10px; !important;
        }
      }
      `}</style>
      <audio ref={audioRef} />

      <ContentLayout
        titleNode="善歌 Lagu Suci"
        hideSearch
        hideLangSwitch={false}
				headerActions={headerActions}
        mainNode={
          <>
            <SongsTopBar
              mode={mode}
              setMode={setMode}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              setShowDialog={setShowDialog}
              selectedPlaylist={selectedPlaylist}
              setSelectedPlaylist={setSelectedPlaylist}
              setFilters={setFilters}
              setAppliedFilters={setAppliedFilters}
              rows={rows}
              isMobile={isMobile}
              canEdit={canEdit}
            />

            {mode === "daftar" && (
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "0.5rem",
                marginLeft: "0.5rem",
                fontSize: "0.85rem",
                color: "#666"
              }}>
                <div>
                  {rows.length} dari {meta.total.toLocaleString()} lagu | Total {meta.total.toLocaleString()} lagu
                </div>

                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem"
                }}>
                  <button
                    className="btn-secondary"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                    style={{ height: "32px" }}
                  >
                    ←
                  </button>

                  <div style={{
                    minWidth: "60px",
                    textAlign: "center",
                    lineHeight: "32px"
                  }}>
                    Page {meta.page}
                  </div>

                  <button
                    className="btn-secondary"
                    disabled={rows.length < 25}
                    onClick={() => setPage(p => p + 1)}
                    style={{ height: "32px" }}
                  >
                    →
                  </button>
                </div>
              </div>
            )}

            <SongsContent
              rows={rows}
              loading={loading}
              mode={mode}
              selectedPlaylist={selectedPlaylist}
              setSelectedPlaylist={setSelectedPlaylist}
              play={play}
              playingId={playingId}
              canEdit={canEdit}
              setEditingData={setEditingData}
              setShowDialog={setShowDialog}
              setFileSong={setFileSong}
              setShareData={setShareData}
              setEditingPlaylist={setEditingPlaylist}
            />
          </>
        }
        filterNode={
          (mode === "song" || mode === "daftar") && (
            <SongFilterPanel
              filters={filters}
              setFilters={setFilters}
              onApply={(f) => setAppliedFilters(f)}
              onClear={(f) => setAppliedFilters(f)}
              isEditorMode={mode === "daftar"} 
            />
          )
        }
      />

      {/* dialogs */}
      {showDialog === "song" && (
        <AdminDialog onClose={() => setShowDialog(null)}>
          <SongForm
            data={editingData}
            onClose={() => setShowDialog(null)}
            onSaved={() => setReloadKey(k => k + 1)}
          />
        </AdminDialog>
      )}

      {showDialog === "filter" && (
        <AdminDialog onClose={() => setShowDialog(null)}>
          <SongFilterPanel
            filters={filters}
            setFilters={setFilters}
            onApply={(f) => {
              setAppliedFilters(f);
              setShowDialog(null);
            }}
          />
        </AdminDialog>
      )}

      {showDialog === "album" && (
        <AdminDialog onClose={() => setShowDialog(null)} title="Album">
          <AlbumForm
            data={editingPlaylist}
            onClose={() => setShowDialog(null)}
            onSaved={() => setReloadKey(k => k + 1)}
          />
        </AdminDialog>
      )}

      {showDialog === "playlistItem" && selectedPlaylist && (
        <AdminDialog onClose={() => setShowDialog(null)} title="Playlist Items">
          <PlaylistItemForm
            playlist={selectedPlaylist}
            onClose={() => setShowDialog(null)}
            onSaved={() => setReloadKey(k => k + 1)}
          />
        </AdminDialog>
      )}

      {fileSong && (
        <AdminDialog title="Manage Files" onClose={() => setFileSong(null)}>
          <SongFileForm
            song={fileSong}
            onClose={() => setFileSong(null)}
            canEdit={canEdit}
            onShare={(file) => setShareData({ type: "file", file })}
          />
        </AdminDialog>
      )}

      {shareData && (
        <AdminDialog title="Share" onClose={() => setShareData(null)}>
          <SongShareDialog
            {...shareData}
            onClose={() => setShareData(null)}
          />
        </AdminDialog>
      )}      
    </>
  );
}